// Campus Asset Management (CAM) API client — ADDITIVE.
// Self-contained so it never risks the existing ticketing api surface. Reuses the
// same bearer token and error shape as @/lib/api.
import { getToken, ApiError } from "@/lib/api";

const API_URL = process.env.REACT_APP_API_URL || "";

async function req(path, { method = "GET", body, query } = {}) {
  const url = new URL(`${API_URL}${path}`);
  if (query) Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
  });
  const headers = new Headers();
  if (body) headers.set("Content-Type", "application/json");
  const t = getToken();
  if (t) headers.set("Authorization", `Bearer ${t}`);

  let res;
  try {
    res = await fetch(url.toString(), { method, headers, body: body ? JSON.stringify(body) : undefined });
  } catch (e) {
    throw new ApiError("Network error — is the API reachable?", 0, { detail: String(e) });
  }
  const text = await res.text();
  let data; try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (!res.ok) throw new ApiError(data?.error || `${method} ${path} failed (${res.status})`, res.status, data);
  return data;
}

export const camApi = {
  context: () => req(`/api/cam/context`),
  myAssets: () => req(`/api/cam/my-assets`),

  // Locations
  tree: () => req(`/api/cam/locations/tree`),
  list: (seg) => req(`/api/cam/${seg}`),                          // campuses|buildings|floors|departments|rooms|labs
  createLoc: (seg, body) => req(`/api/cam/${seg}`, { method: "POST", body }),
  updateLoc: (seg, id, body) => req(`/api/cam/${seg}/${id}`, { method: "PATCH", body }),
  removeLoc: (seg, id) => req(`/api/cam/${seg}/${id}`, { method: "DELETE" }),
  seats: (labId) => req(`/api/cam/labs/${labId}/seats`),
  genSeats: (labId, body) => req(`/api/cam/labs/${labId}/seats`, { method: "POST", body }),
  labLayout: (labId) => req(`/api/cam/labs/${labId}/layout`),
  placeAsset: (id, body) => req(`/api/cam/assets/${id}/place`, { method: "POST", body }),

  // Categories + fields
  categories: () => req(`/api/cam/categories`),
  createCategory: (body) => req(`/api/cam/categories`, { method: "POST", body }),
  updateCategory: (id, body) => req(`/api/cam/categories/${id}`, { method: "PATCH", body }),
  removeCategory: (id) => req(`/api/cam/categories/${id}`, { method: "DELETE" }),
  addField: (catId, body) => req(`/api/cam/categories/${catId}/fields`, { method: "POST", body }),
  updateField: (id, body) => req(`/api/cam/category-fields/${id}`, { method: "PATCH", body }),
  removeField: (id) => req(`/api/cam/category-fields/${id}`, { method: "DELETE" }),
  addSubcategory: (catId, body) => req(`/api/cam/categories/${catId}/subcategories`, { method: "POST", body }),
  updateSubcategory: (id, body) => req(`/api/cam/subcategories/${id}`, { method: "PATCH", body }),
  removeSubcategory: (id) => req(`/api/cam/subcategories/${id}`, { method: "DELETE" }),

  // CAM roles
  roles: () => req(`/api/cam/roles`),
  assignRole: (body) => req(`/api/cam/roles`, { method: "POST", body }),
  updateRole: (id, body) => req(`/api/cam/roles/${id}`, { method: "PATCH", body }),
  removeRole: (id) => req(`/api/cam/roles/${id}`, { method: "DELETE" }),

  // Assets
  assets: (query = {}) => req(`/api/cam/assets`, { query }),
  asset: (id) => req(`/api/cam/assets/${id}`),
  createAsset: (body) => req(`/api/cam/assets`, { method: "POST", body }),
  updateAsset: (id, body) => req(`/api/cam/assets/${id}`, { method: "PATCH", body }),
  removeAsset: (id) => req(`/api/cam/assets/${id}`, { method: "DELETE" }),
  setStatus: (id, status, note) => req(`/api/cam/assets/${id}/status`, { method: "POST", body: { status, note } }),
  assign: (id, body) => req(`/api/cam/assets/${id}/assign`, { method: "POST", body }),
  bulkAssign: (body) => req(`/api/cam/assets/bulk-assign`, { method: "POST", body }),
  bulkImport: (rows, base_row = 0) => req(`/api/cam/assets/bulk-import`, { method: "POST", body: { rows, base_row } }),
  saveNetwork: (id, body) => req(`/api/cam/assets/${id}/network`, { method: "PUT", body }),

  // Transfers
  transfers: (query = {}) => req(`/api/cam/transfers`, { query }),
  requestTransfer: (body) => req(`/api/cam/transfers`, { method: "POST", body }),
  decideTransfer: (id, decision, note) => req(`/api/cam/transfers/${id}/decide`, { method: "POST", body: { decision, note } }),

  // Repairs / work orders
  repairs: (query = {}) => req(`/api/cam/repairs`, { query }),
  createRepair: (body) => req(`/api/cam/repairs`, { method: "POST", body }),
  updateRepair: (id, body) => req(`/api/cam/repairs/${id}`, { method: "PATCH", body }),

  // Maintenance schedules
  maintenance: (query = {}) => req(`/api/cam/maintenance`, { query }),
  createSchedule: (body) => req(`/api/cam/maintenance`, { method: "POST", body }),
  updateSchedule: (id, body) => req(`/api/cam/maintenance/${id}`, { method: "PATCH", body }),
  removeSchedule: (id) => req(`/api/cam/maintenance/${id}`, { method: "DELETE" }),

  // Audits
  audits: () => req(`/api/cam/audits`),
  audit: (id) => req(`/api/cam/audits/${id}`),
  createAudit: (body) => req(`/api/cam/audits`, { method: "POST", body }),
  verifyAudit: (id, body) => req(`/api/cam/audits/${id}/verify`, { method: "POST", body }),
  completeAudit: (id) => req(`/api/cam/audits/${id}/complete`, { method: "POST" }),

  // Reports
  summary: () => req(`/api/cam/reports/summary`),
  warranty: (days = 60) => req(`/api/cam/reports/warranty`, { query: { days } }),
  disposal: () => req(`/api/cam/reports/disposal`),
  dashboard: () => req(`/api/cam/dashboard`),

  // Vendors
  vendors: () => req(`/api/cam/vendors`),
  createVendor: (body) => req(`/api/cam/vendors`, { method: "POST", body }),
  updateVendor: (id, body) => req(`/api/cam/vendors/${id}`, { method: "PATCH", body }),
  removeVendor: (id) => req(`/api/cam/vendors/${id}`, { method: "DELETE" }),

  // AMC contracts
  amc: (query = {}) => req(`/api/cam/amc`, { query }),
  createAmc: (body) => req(`/api/cam/amc`, { method: "POST", body }),
  updateAmc: (id, body) => req(`/api/cam/amc/${id}`, { method: "PATCH", body }),
  removeAmc: (id) => req(`/api/cam/amc/${id}`, { method: "DELETE" }),

  // Ticketing — the EXISTING pipeline. An asset ticket is a normal ticket that
  // additionally carries cam_asset_id, so it routes, SLAs and notifies as usual.
  ticketCategories: () => req(`/api/categories`),
  raiseTicket: (body) => req(`/api/tickets`, { method: "POST", body }),
};

export default camApi;

// Shared constants (kept here so pages don't duplicate them)
export const CAM_STATUSES = ["in_store", "installed", "assigned", "in_repair", "in_maintenance", "transferred", "disposed"];
export const CAM_CONDITIONS = ["new", "good", "fair", "poor", "damaged"];
export const STATUS_META = {
  in_store:       { label: "In store",       color: "#64748b" },
  installed:      { label: "Installed",      color: "#0654FC" },
  assigned:       { label: "Assigned",       color: "#7c3aed" },
  in_repair:      { label: "In repair",      color: "#e6a700" },
  in_maintenance: { label: "Maintenance",    color: "#d97706" },
  transferred:    { label: "Transferred",    color: "#0891b2" },
  disposed:       { label: "Disposed",       color: "#ef4444" },
};
export const CONDITION_META = {
  new:     { color: "#16a34a" }, good: { color: "#22c55e" }, fair: { color: "#eab308" },
  poor:    { color: "#f97316" }, damaged: { color: "#ef4444" },
};
